import React, { useState, useEffect, useCallback } from 'react';
import { getTipoCreditoById, createTipoCredito, updateTipoCredito } from '../services/tipoCreditoService';
import { getPlanCuentas } from '../services/planCuentaService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, RefreshCw, Calculator, Search, Check, AlertCircle, PlusCircle, FileText } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

// Modal de Búsqueda de Cuentas Contables (Optimizado)
const AccountSearchModal = ({ isOpen, onClose, onSelect, title, currentAccountId }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const { token } = useAuth();

    const fetchAccounts = useCallback(async () => {
        if (!isOpen) return;
        try {
            setLoading(true);
            const data = await getPlanCuentas(token, searchTerm);
            setAccounts(data.slice(0, 50)); // Limitamos para rendimiento
        } catch (error) {
            console.error('Error fetching accounts:', error);
        } finally {
            setLoading(false);
        }
    }, [token, searchTerm, isOpen]);

    useEffect(() => {
        const timer = setTimeout(fetchAccounts, 300);
        return () => clearTimeout(timer);
    }, [fetchAccounts]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-4">
            <div className="bg-white shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-300">
                <div className="bg-slate-800 p-3 flex justify-between items-center text-white">
                    <h3 className="text-xs font-bold uppercase flex items-center gap-2 tracking-widest">
                        <Calculator className="w-4 h-4 text-blue-400" />
                        {title}
                    </h3>
                    <button onClick={onClose} className="hover:text-red-400 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-3 bg-slate-50 border-b border-slate-200">
                    <div className="relative">
                        <input
                            autoFocus
                            type="text"
                            placeholder="Filtrar por código o nombre..."
                            className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-none focus:ring-1 focus:ring-blue-500 outline-none text-sm font-medium"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
                    </div>
                </div>

                <div className="max-h-[350px] overflow-y-auto">
                    {loading ? (
                        <div className="p-10 text-center"><RefreshCw className="animate-spin mx-auto w-6 h-6 text-blue-500" /></div>
                    ) : accounts.length === 0 ? (
                        <div className="p-10 text-center text-slate-400 italic text-sm font-bold uppercase tracking-tighter">No se encontraron cuentas.</div>
                    ) : (
                        <table className="w-full text-left text-[12px]">
                            <thead className="bg-slate-100 sticky top-0 text-[10px] uppercase text-slate-500 font-black border-b border-slate-200">
                                <tr>
                                    <th className="px-4 py-2 tracking-widest w-32">Código</th>
                                    <th className="px-4 py-2 tracking-widest">Nombre de Cuenta</th>
                                    <th className="px-1 py-1 w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.map(acc => (
                                    <tr
                                        key={acc.id}
                                        className={`border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors ${acc.id === currentAccountId ? 'bg-blue-100' : ''}`}
                                        onClick={() => onSelect(acc)}
                                    >
                                        <td className="px-4 py-2.5 font-mono font-bold text-blue-700">{acc.codigoIncoop}</td>
                                        <td className="px-4 py-2.5 text-slate-700 font-bold uppercase">{acc.nombreCuenta}</td>
                                        <td className="px-4 py-2.5 text-center">
                                            {acc.id === currentAccountId ? <Check className="w-4 h-4 text-green-600" /> : <PlusCircle className="w-4 h-4 text-slate-300" />}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="p-2 bg-slate-50 border-t border-slate-200 text-right">
                    <button onClick={onClose} className="text-[10px] font-black uppercase text-slate-500 hover:text-slate-800 px-4 py-1">Cerrar</button>
                </div>
            </div>
        </div>
    );
};

export default function TipoCreditoForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        tasaReferencia: '',
        cuentaContableCapitalId: null,
        cuentaContableCapitalNombre: '',
        cuentaContableCapitalCodigo: '',
        cuentaContableInteresId: null,
        cuentaContableInteresNombre: '',
        cuentaContableInteresCodigo: ''
    });

    const [loading, setLoading] = useState(false);
    const [modalConfig, setModalConfig] = useState({ isOpen: false, field: '', title: '' });

    useEffect(() => {
        if (isEditMode) {
            cargarTipo();
        }
    }, [id]);

    const cargarTipo = async () => {
        try {
            setLoading(true);
            const data = await getTipoCreditoById(token, id);
            setFormData({
                nombre: data.nombre,
                descripcion: data.descripcion || '',
                tasaReferencia: data.tasaReferencia ? Number(data.tasaReferencia).toFixed(2) : '',
                cuentaContableCapitalId: data.cuentaContableCapitalId,
                cuentaContableCapitalNombre: data.cuentaContableCapitalNombre,
                cuentaContableCapitalCodigo: data.cuentaContableCapitalCodigo,
                cuentaContableInteresId: data.cuentaContableInteresId,
                cuentaContableInteresNombre: data.cuentaContableInteresNombre,
                cuentaContableInteresCodigo: data.cuentaContableInteresCodigo
            });
        } catch (error) {
            console.error('Error al cargar:', error);
            toast.error('Error al cargar los datos');
            navigate('/parametrizacion/tipos-credito');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAccount = (account) => {
        const field = modalConfig.targetField;

        // Validación: Cuenta de Capital != Cuenta de Interés
        if (field === 'Capital' && account.id === formData.cuentaContableInteresId) {
            return toast.error('La cuenta de activo (capital) debe ser distinta a la de ingresos (interés)');
        }
        if (field === 'Interes' && account.id === formData.cuentaContableCapitalId) {
            return toast.error('La cuenta de ingresos (interés) debe ser distinta a la de activo (capital)');
        }

        setFormData(prev => ({
            ...prev,
            [`cuentaContable${field}Id`]: account.id,
            [`cuentaContable${field}Nombre`]: account.nombreCuenta,
            [`cuentaContable${field}Codigo`]: account.codigoIncoop
        }));
        setModalConfig({ isOpen: false, targetField: '', title: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre.trim()) return toast.error('El nombre es requerido');
        if (!formData.cuentaContableCapitalId) return toast.error('La cuenta contable de capital es requerida');
        if (!formData.cuentaContableInteresId) return toast.error('La cuenta contable de interés es requerida');

        // Refuerzo de validación en submit
        if (formData.cuentaContableCapitalId === formData.cuentaContableInteresId) {
            return toast.error('La cuenta de ingresos (interés) debe ser distinta a la de activo (capital)');
        }

        try {
            setLoading(true);
            if (isEditMode) {
                await updateTipoCredito(token, id, formData);
                toast.success('Tipo de crédito actualizado');
            } else {
                await createTipoCredito(token, formData);
                toast.success('Tipo de crédito creado');
            }
            setTimeout(() => navigate('/parametrizacion/tipos-credito'), 1000);
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.error('Error al procesar la solicitud');
        } finally {
            setLoading(false);
        }
    };

    const AccountCard = ({ field, title, id, code, name }) => (
        <div className="bg-white border border-slate-200 p-4 relative hover:border-blue-400 transition-all shadow-sm">
            <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1 border-b border-slate-100 pb-1">
                <Calculator className="w-3.5 h-3.5 text-blue-500" />
                {title}
            </h3>
            {id ? (
                <div className="flex justify-between items-center group">
                    <div className="truncate pr-4">
                        <div className="text-[13px] font-black text-blue-700 font-mono tracking-tighter leading-none">{code}</div>
                        <div className="text-[11px] text-slate-800 font-bold uppercase mt-1 truncate">{name}</div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setModalConfig({ isOpen: true, targetField: field, title: `Asociar ${title}` })}
                        className="p-2 bg-slate-50 border border-slate-200 hover:bg-blue-50 text-blue-600 transition-all rounded-none shadow-sm"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setModalConfig({ isOpen: true, targetField: field, title: `Vincular ${title}` })}
                    className="w-full py-6 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-all bg-[#fafafa]"
                >
                    <PlusCircle className="w-6 h-6 mb-2 opacity-50" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Vincular Cuenta Contable</span>
                </button>
            )}
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-xl border border-slate-300 mb-10 overflow-hidden rounded-none">
            <Toaster position="top-right" />

            {/* Modal de Búsqueda */}
            <AccountSearchModal
                isOpen={modalConfig.isOpen}
                title={modalConfig.title}
                targetField={modalConfig.targetField}
                currentAccountId={formData[`cuentaContable${modalConfig.targetField}Id`]}
                onClose={() => setModalConfig({ isOpen: false, targetField: '', title: '' })}
                onSelect={handleSelectAccount}
            />

            {/* Header Enterprise Estilo Oracle APEX */}
            <div className="bg-slate-700 p-4 flex items-center justify-between text-white shadow-inner border-b border-slate-800">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/parametrizacion/tipos-credito')}
                        className="p-1.5 hover:bg-slate-600 transition-colors text-slate-300 hover:text-white"
                    >
                        <ArrowLeft className="w-6 h-6 shadow-sm" />
                    </button>
                    <div>
                        <h1 className="text-lg font-black tracking-tight uppercase">
                            {isEditMode ? 'Mantenimiento: ' + formData.nombre : 'Definición de Nuevo Tipo de Crédito'}
                        </h1>
                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.3em]">Módulo de Dependencia Contable Maestra</p>
                    </div>
                </div>
                <div className="bg-slate-600 px-3 py-1 text-[10px] font-black uppercase border border-slate-500">Mv-TCRED-0.2</div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 bg-[#f8fafc]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

                    {/* BLOQUE IZQUIERDO: Definición del Negocio */}
                    <div className="space-y-8">
                        <div className="bg-white border border-slate-200 p-6 relative overflow-hidden shadow-sm">
                            <div className="absolute top-0 left-0 w-1 h-full bg-slate-700"></div>
                            <div className="flex items-center gap-2 mb-6 border-b border-slate-100 pb-2">
                                <FileText className="w-4 h-4 text-slate-400" />
                                <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Información Básica del Producto</h3>
                            </div>

                            <div className="space-y-5">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Nombre Identificador <span className="text-red-500">*</span></label>
                                    <input
                                        type="text" required
                                        className="w-full px-4 py-2 border border-slate-300 focus:border-blue-600 outline-none font-bold uppercase bg-slate-50 focus:bg-white transition-all text-[13px] rounded-none shadow-sm"
                                        placeholder="EJ: CONSUMO PERSONAL, EMPRESARIAL"
                                        value={formData.nombre}
                                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-1">
                                        <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Tasa de Referencia (%)</label>
                                        <div className="relative">
                                            <input
                                                type="number" step="0.01"
                                                className="w-full pl-3 pr-8 py-2 border border-slate-300 focus:border-blue-600 outline-none text-[13px] font-black bg-slate-50 focus:bg-white text-right rounded-none shadow-sm"
                                                value={formData.tasaReferencia}
                                                onChange={(e) => setFormData({ ...formData, tasaReferencia: e.target.value })}
                                            />
                                            <span className="absolute right-3 top-2 text-slate-400 font-bold text-xs">%</span>
                                        </div>
                                    </div>
                                    <div className="col-span-1 border-l border-slate-100 pl-4 bg-blue-50/50 p-2 flex items-center">
                                        <p className="text-[9px] text-blue-700 font-medium italic leading-tight uppercase font-black tracking-tighter">Este valor se heredará como sugerencia en las líneas comerciales.</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Alcance / Descripción Contable</label>
                                    <textarea
                                        rows="3"
                                        className="w-full px-4 py-3 border border-slate-300 focus:border-blue-600 outline-none text-[12px] italic text-slate-600 bg-white transition-all border-dashed rounded-none shadow-sm resize-none"
                                        placeholder="Describa el propósito operativo..."
                                        value={formData.descripcion}
                                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BLOQUE DERECHO: Vínculo Contable (Esencial) */}
                    <div className="space-y-6 bg-slate-200/50 p-6 border border-slate-300 shadow-inner">
                        <div className="text-center mb-4">
                            <h2 className="text-xs font-black text-slate-700 uppercase tracking-[0.2em]">Configuración Contable Maestra</h2>
                            <div className="h-1 w-12 bg-blue-600 mx-auto mt-1"></div>
                        </div>

                        <div className="space-y-6">
                            <AccountCard
                                field="Capital"
                                title="Cuenta Activo (Cartera Capital)"
                                id={formData.cuentaContableCapitalId}
                                code={formData.cuentaContableCapitalCodigo}
                                name={formData.cuentaContableCapitalNombre}
                            />

                            <AccountCard
                                field="Interes"
                                title="Cuenta Ingresos (Interés Devengado)"
                                id={formData.cuentaContableInteresId}
                                code={formData.cuentaContableInteresCodigo}
                                name={formData.cuentaContableInteresNombre}
                            />

                            <div className="bg-amber-50 border border-amber-200 p-4 shadow-sm flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
                                <div className="text-[10px] text-amber-800 font-bold uppercase leading-relaxed">
                                    <p className="mb-1">⚠️ IMPORTANTE:</p>
                                    Cualquier cambio en estas cuentas afectará inmediatamente a todas las Líneas de Crédito que hereden de este tipo, a menos que tengan overrides configurados.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer del Formulario */}
                <div className="mt-12 pt-6 border-t border-slate-300 flex justify-between items-center">
                    <button
                        type="button"
                        onClick={() => navigate('/parametrizacion/tipos-credito')}
                        className="px-8 py-2 border border-slate-300 text-slate-500 hover:bg-slate-200 font-black text-[11px] uppercase transition-all shadow-sm rounded-none"
                    >
                        Cancelar y Salir
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="px-12 py-3 bg-[#0066cc] text-white shadow-lg hover:bg-[#004d99] transition-all font-black text-[12px] uppercase tracking-[0.1em] flex items-center gap-2 border border-[#0052a3] shadow-[0_4px_10px_rgba(0,102,204,0.3)] disabled:opacity-50"
                    >
                        {loading && <RefreshCw className="w-5 h-5 animate-spin" />}
                        {!loading && <Check className="w-5 h-5" />}
                        {isEditMode ? 'Consolidar Cambios' : 'Confirmar Definición'}
                    </button>
                </div>
            </form>
        </div>
    );
}
